import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { toast } from '@eureca/ui';
import { revalidateLogic, useForm, useFormErrors, useStore } from '@eureca/ui/forms';
import { z } from 'zod';
import { JobEditCompensationPage as JobEditCompensationPageComponent } from './JobEditCompensationPage.component';
import type { JobEditCompensationValues } from './JobEditCompensationPage.component';
import { JobEditCompensationLoadingFallback } from './JobEditCompensationLoadingFallback';
import { JobEditCompensationErrorFallback } from './JobEditCompensationErrorFallback';
import { AddItemDialog } from './AddItemDialog';
import {
  ADDITIONAL_PAYMENT_OPTIONS,
  BENEFIT_OPTIONS,
  emptyValues,
} from './JobEditCompensationPage.mock';
import { formatCentsToBrInput, parseBrCurrencyToCents } from './currency';
import { JobFetchError, jobByIdQueryOptions } from '@/features/jobs/job-shared/job-by-id-query';
import { isJobDetailReadOnly } from '@/features/jobs/job-shared/job-status';
import { JobMutationError, usePatchJob } from '@/features/jobs/job-shared/job-edit-mutation';
import { DEFAULT_JOBS_SEARCH } from '@/features/jobs/jobs-list/jobs-search.schema';
import { useJobCompanyLogo } from '@/features/jobs/job-shared/use-job-company-logo';

type AddDialog = 'benefit' | 'additional-payment' | null;
type SalaryDisplay = 'valor-exato' | 'faixa-salarial' | 'nao-divulgar';

// EU-161 + bugs Juliana QA: `superRefine` emite erros de obrigatoriedade
// por display (`salary` quando `valor-exato`; `salaryMin` + `salaryMax`
// quando `faixa-salarial`) + cross-field `max > min`. O submit é gateado
// por `canSubmit` (botão `disabled`); o `buildPayload` mantém a omissão
// defensiva da faixa quando inválida como belt-and-suspenders, mas o
// caminho normal é o submit não disparar enquanto houver erro ativo.
const schema = z
  .object({
    salaryDisplay: z.string(),
    salary: z.string(),
    salaryMin: z.string(),
    salaryMax: z.string(),
    selectedBenefits: z.array(z.string()),
    selectedAdditionalPayments: z.array(z.string()),
  })
  .superRefine((value, ctx) => {
    if (value.salaryDisplay === 'valor-exato') {
      if (!value.salary.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['salary'],
          message: 'Informe o salário',
        });
      }
    }
    if (value.salaryDisplay === 'faixa-salarial') {
      if (!value.salaryMin.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['salaryMin'],
          message: 'Informe o valor mínimo',
        });
      }
      if (!value.salaryMax.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['salaryMax'],
          message: 'Informe o valor máximo',
        });
      }
      if (value.salaryMin.trim() && value.salaryMax.trim()) {
        const min = parseBrCurrencyToCents(value.salaryMin);
        const max = parseBrCurrencyToCents(value.salaryMax);
        if (min !== null && max !== null && max <= min) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['salaryMax'],
            message: 'Este valor deve ser maior que o mínimo digitado anteriormente.',
          });
        }
      }
    }
  });

interface PatchPayload {
  salaryDisplay?: SalaryDisplay | null;
  salaryMinCents?: number | null;
  salaryMaxCents?: number | null;
  benefitNames?: string[];
  additionalPaymentNames?: string[];
}

// Gate cross-field: quando `display='faixa-salarial' && max <= min`, OMITIR
// `salaryMinCents` E `salaryMaxCents` (PATCH style — Core mantém estado
// antigo da faixa). Outros campos (`salaryDisplay`, `benefitNames`, etc.)
// seguem salvando.
function buildPayload(values: JobEditCompensationValues): PatchPayload {
  const display = (values.salaryDisplay || null) as SalaryDisplay | null;
  let salaryMinCents: number | null = null;
  let salaryMaxCents: number | null = null;
  if (display === 'valor-exato') {
    salaryMinCents = parseBrCurrencyToCents(values.salary);
  } else if (display === 'faixa-salarial') {
    salaryMinCents = parseBrCurrencyToCents(values.salaryMin);
    salaryMaxCents = parseBrCurrencyToCents(values.salaryMax);
  }
  const payload: PatchPayload = {
    salaryDisplay: display,
    benefitNames: values.selectedBenefits,
    additionalPaymentNames: values.selectedAdditionalPayments,
  };
  const faixaInvalid =
    display === 'faixa-salarial' &&
    salaryMinCents !== null &&
    salaryMaxCents !== null &&
    salaryMaxCents <= salaryMinCents;
  if (!faixaInvalid) {
    payload.salaryMinCents = salaryMinCents;
    payload.salaryMaxCents = salaryMaxCents;
  }
  return payload;
}

/**
 * JRN-046 — chave normalizada para dedupe de chips.
 */
function normalizeKey(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '');
}

function mergeByNormalizedKey(...lists: readonly (readonly string[])[]): string[] {
  const result: string[] = [];
  const seen = new Set<string>();
  for (const list of lists) {
    for (const item of list) {
      const key = normalizeKey(item);
      if (key.length === 0 || seen.has(key)) continue;
      seen.add(key);
      result.push(item);
    }
  }
  return result;
}

function includesNormalized(list: readonly string[], value: string): boolean {
  const target = normalizeKey(value);
  for (const item of list) {
    if (normalizeKey(item) === target) return true;
  }
  return false;
}

function canonicalizeAgainst(selected: readonly string[], catalog: readonly string[]): string[] {
  const result: string[] = [];
  const seen = new Set<string>();
  for (const item of selected) {
    const key = normalizeKey(item);
    if (key.length === 0 || seen.has(key)) continue;
    seen.add(key);
    const canonical = catalog.find((c) => normalizeKey(c) === key);
    result.push(canonical ?? item);
  }
  return result;
}

export default function JobEditCompensationPage() {
  const navigate = useNavigate();
  const { jobId } = useParams({
    from: '/vagas/$jobId/editar/remuneracao',
  });

  const jobQuery = useQuery(jobByIdQueryOptions(jobId));
  const patchJob = usePatchJob(jobId);
  const [hasHydrated, setHasHydrated] = useState(false);

  const job = jobQuery.data;
  // EU-569 — vaga Encerrada/Cancelada: seção em modo somente visualização.
  const readOnly = isJobDetailReadOnly(job);
  const jobName = job?.name ?? '';
  const { companyLogoUrl, companyName } = useJobCompanyLogo(job?.programId);

  const lockedBenefits = useMemo(() => job?.programBenefitNames ?? [], [job?.programBenefitNames]);
  const lockedAdditionalPayments = useMemo(
    () => job?.programAdditionalPaymentNames ?? [],
    [job?.programAdditionalPaymentNames]
  );

  const initialBenefitOptions = useMemo(() => {
    const fromJob = job?.benefitNames ?? [];
    return mergeByNormalizedKey(lockedBenefits, BENEFIT_OPTIONS, fromJob);
  }, [job?.benefitNames, lockedBenefits]);
  const initialAdditionalPaymentOptions = useMemo(() => {
    const fromJob = job?.additionalPaymentNames ?? [];
    return mergeByNormalizedKey(lockedAdditionalPayments, ADDITIONAL_PAYMENT_OPTIONS, fromJob);
  }, [job?.additionalPaymentNames, lockedAdditionalPayments]);

  const [benefitOptions, setBenefitOptions] = useState<string[]>(() => [...BENEFIT_OPTIONS]);
  const [additionalPaymentOptions, setAdditionalPaymentOptions] = useState<string[]>(() => [
    ...ADDITIONAL_PAYMENT_OPTIONS,
  ]);
  const [activeDialog, setActiveDialog] = useState<AddDialog>(null);

  async function persist(values: JobEditCompensationValues) {
    try {
      await patchJob.mutateAsync(buildPayload(values));
      toast('Alterações salvas com sucesso', {
        action: { label: 'Fechar', onClick: () => undefined },
      });
      void navigate({
        to: '/vagas/$jobId',
        params: { jobId },
      });
    } catch (err) {
      const status = err instanceof JobMutationError ? err.status : 0;
      if (status === 404) {
        toast.error('Vaga não encontrada.');
      } else if (status === 401 || status === 403) {
        toast.error('Você não tem permissão para editar essa vaga.');
      } else {
        toast.error('Não foi possível salvar as alterações. Tente novamente.');
      }
    }
  }

  const form = useForm({
    defaultValues: emptyValues satisfies JobEditCompensationValues,
    // Eager validation — erros de obrigatoriedade + cross-field aparecem
    // inline assim que os valores são preenchidos. O submit do botão
    // "Salvar alterações" é gateado por `canSubmit` (disabled quando há
    // erro ativo) e chama `persist(values)` direto — `buildPayload` mantém
    // a omissão defensiva da faixa inválida como belt-and-suspenders.
    validationLogic: revalidateLogic({ mode: 'change', modeAfterSubmission: 'change' }),
    validators: { onDynamic: schema },
    onSubmit: async () => {
      /* no-op */
    },
  });

  // Hidratação one-shot.
  useEffect(() => {
    if (hasHydrated || !job) return;
    const display = job.salaryDisplay ?? '';
    const hydrated: JobEditCompensationValues = {
      salaryDisplay: display,
      salary: display === 'valor-exato' ? formatCentsToBrInput(job.salaryMinCents) : '',
      salaryMin: display === 'faixa-salarial' ? formatCentsToBrInput(job.salaryMinCents) : '',
      salaryMax: display === 'faixa-salarial' ? formatCentsToBrInput(job.salaryMaxCents) : '',
      selectedBenefits: canonicalizeAgainst(
        mergeByNormalizedKey(lockedBenefits, job.benefitNames ?? []),
        initialBenefitOptions
      ),
      selectedAdditionalPayments: canonicalizeAgainst(
        mergeByNormalizedKey(lockedAdditionalPayments, job.additionalPaymentNames ?? []),
        initialAdditionalPaymentOptions
      ),
    };
    form.setFieldValue('salaryDisplay', hydrated.salaryDisplay);
    form.setFieldValue('salary', hydrated.salary);
    form.setFieldValue('salaryMin', hydrated.salaryMin);
    form.setFieldValue('salaryMax', hydrated.salaryMax);
    form.setFieldValue('selectedBenefits', hydrated.selectedBenefits);
    form.setFieldValue('selectedAdditionalPayments', hydrated.selectedAdditionalPayments);
    setBenefitOptions(initialBenefitOptions);
    setAdditionalPaymentOptions(initialAdditionalPaymentOptions);
    setHasHydrated(true);
  }, [
    job,
    hasHydrated,
    form,
    initialBenefitOptions,
    initialAdditionalPaymentOptions,
    lockedBenefits,
    lockedAdditionalPayments,
  ]);

  useEffect(() => {
    if (!jobQuery.isError) return;
    const status = jobQuery.error instanceof JobFetchError ? jobQuery.error.status : 0;
    if (status === 404) {
      toast('Vaga não encontrada.');
      void navigate({ to: '/vagas', search: DEFAULT_JOBS_SEARCH });
    }
  }, [jobQuery.isError, jobQuery.error, navigate]);

  const values = useStore(form.store, (state) => state.values);
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const errors = useFormErrors(form);
  // Gate global: botão Submit `disabled` enquanto houver erro ativo em
  // qualquer um dos campos de salário (obrigatoriedade por display ou
  // cross-field `max > min`). Paridade com V10/EU-163 — fix do bug QA
  // Juliana em que outros campos seguiam salvando mesmo com a faixa
  // inválida.
  const canSubmit = !errors.salary && !errors.salaryMin && !errors.salaryMax;

  // EU-709 RN-14 — a seção é opcional quando a API assim disser (Banco de
  // talentos). Sem contrato, segue como hoje.
  const sectionOptional = job?.requirements?.optionalSections?.includes('compensation') ?? false;
  // RN-21 — "há algo a salvar". Qualquer campo preenchido conta: tipo de
  // divulgação, valores, benefício ou pagamento adicional. É o que separa
  // "ainda não mexi em nada" de "mexi e quero gravar" — o `Voltar` continua
  // sendo o caminho para sair sem salvar.
  const hasSomethingToSave =
    values.salaryDisplay.trim() !== '' ||
    values.salary.trim() !== '' ||
    values.salaryMin.trim() !== '' ||
    values.salaryMax.trim() !== '' ||
    values.selectedBenefits.length > 0 ||
    values.selectedAdditionalPayments.length > 0;

  function handleBack() {
    void navigate({
      to: '/vagas/$jobId',
      params: { jobId },
    });
  }

  function handleFieldChange<K extends keyof JobEditCompensationValues>(
    field: K,
    value: JobEditCompensationValues[K]
  ) {
    form.setFieldValue(field, value as never);
    if (field === 'salaryDisplay') {
      if (value === 'valor-exato') {
        form.setFieldValue('salaryMin', '');
        form.setFieldValue('salaryMax', '');
      } else if (value === 'faixa-salarial') {
        form.setFieldValue('salary', '');
      } else {
        form.setFieldValue('salary', '');
        form.setFieldValue('salaryMin', '');
        form.setFieldValue('salaryMax', '');
      }
    }
  }

  function toggleSelection(list: string[], value: string): string[] {
    return list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value];
  }

  function addIfMissing(list: string[], value: string): string[] {
    return list.includes(value) ? list : [...list, value];
  }

  function handleToggleBenefit(value: string) {
    if (includesNormalized(lockedBenefits, value)) return;
    form.setFieldValue('selectedBenefits', (prev) => toggleSelection(prev, value));
  }

  function handleToggleAdditionalPayment(value: string) {
    if (includesNormalized(lockedAdditionalPayments, value)) return;
    form.setFieldValue('selectedAdditionalPayments', (prev) => toggleSelection(prev, value));
  }

  function handleConfirmAddBenefit(value: string) {
    setBenefitOptions((prev) => addIfMissing(prev, value));
    form.setFieldValue('selectedBenefits', (prev) => addIfMissing(prev, value));
    setActiveDialog(null);
    toast('Benefício criado com sucesso', {
      action: { label: 'Fechar', onClick: () => undefined },
    });
  }

  function handleConfirmAddAdditionalPayment(value: string) {
    setAdditionalPaymentOptions((prev) => addIfMissing(prev, value));
    form.setFieldValue('selectedAdditionalPayments', (prev) => addIfMissing(prev, value));
    setActiveDialog(null);
    toast('Pagamento adicional criado com sucesso', {
      action: { label: 'Fechar', onClick: () => undefined },
    });
  }

  if (jobQuery.isLoading) {
    return <JobEditCompensationLoadingFallback />;
  }

  if (jobQuery.isError) {
    const status = jobQuery.error instanceof JobFetchError ? jobQuery.error.status : 0;
    if (status !== 404) {
      return <JobEditCompensationErrorFallback onRetry={() => void jobQuery.refetch()} />;
    }
  }

  return (
    <>
      <JobEditCompensationPageComponent
        jobName={jobName}
        companyLogoUrl={companyLogoUrl}
        companyName={companyName}
        values={values}
        errors={errors}
        isSubmitting={isSubmitting || patchJob.isPending}
        canSubmit={canSubmit}
        sectionOptional={sectionOptional}
        hasSomethingToSave={hasSomethingToSave}
        readOnly={readOnly}
        benefitOptions={benefitOptions}
        lockedBenefits={lockedBenefits}
        additionalPaymentOptions={additionalPaymentOptions}
        lockedAdditionalPayments={lockedAdditionalPayments}
        onFieldChange={handleFieldChange}
        onToggleBenefit={handleToggleBenefit}
        onToggleAdditionalPayment={handleToggleAdditionalPayment}
        onAddBenefit={() => setActiveDialog('benefit')}
        onAddAdditionalPayment={() => setActiveDialog('additional-payment')}
        onBack={handleBack}
        onSubmit={() => {
          // EU-569 — modo visualização: nada persiste.
          if (readOnly) return;
          // Bypass `form.handleSubmit` — `canSubmit` já bloqueia o botão
          // quando há erro ativo. Defensivo aqui contra Enter num input
          // (que ainda dispara `onSubmit` do <form>): se algum erro
          // existe, descarta antes de tocar no Core.
          if (!canSubmit) return;
          void persist(values);
        }}
      />
      <AddItemDialog
        open={activeDialog === 'benefit'}
        onOpenChange={(open) => setActiveDialog(open ? 'benefit' : null)}
        title="Adicionar benefício"
        placeholder="Ex.: Auxílio-Pet"
        onAdd={handleConfirmAddBenefit}
        data-testid="job-compensation-add-benefit"
      />
      <AddItemDialog
        open={activeDialog === 'additional-payment'}
        onOpenChange={(open) => setActiveDialog(open ? 'additional-payment' : null)}
        title="Adicionar pagamento adicionais"
        placeholder="Ex.: 13º salário"
        onAdd={handleConfirmAddAdditionalPayment}
        data-testid="job-compensation-add-additional-payment"
      />
    </>
  );
}
